import React, { useEffect, useMemo, useState } from 'react';
import { useStore } from '../context/StoreContext';
import returnsToSupplierService from '../services/returnsToSupplierService';
import supplierService from '../services/supplierService';
import DateRangeFilter from '../components/common/DateRangeFilter';
import useStoreTimezone from '../hooks/useStoreTimezone';
import { formatInStoreTimezone } from '../utils/dateUtils';

const formatDate = (value, timezone) => {
  if (!value) return '';
  return formatInStoreTimezone(value, 'dd-MM-yyyy hh:mm a', timezone);
};

const ReturnsToSupplierReportPage = () => {
  const { activeStore } = useStore();
  const timezone = useStoreTimezone();
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    supplierId: '',
  });
  const [suppliers, setSuppliers] = useState([]);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({ totalReturns: 0, totalQuantity: 0, totalItems: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const loadSuppliers = async () => {
      try {
        const response = await supplierService.fetchSuppliers({ page: 1, limit: 200, compact: true });
        setSuppliers(response?.data || response || []);
      } catch (err) {
        console.warn('Unable to load suppliers for report', err);
      }
    };
    loadSuppliers();
  }, []);

  useEffect(() => {
    const fetchReport = async () => {
      if (!activeStore?._id) return;
      setLoading(true);
      setError('');
      try {
        const response = await returnsToSupplierService.getReturnReport({
          storeId: activeStore._id,
          ...filters,
        });
        setRows(response.data || response?.data?.data || []);
        setSummary(response.summary || response?.data?.summary || { totalReturns: 0, totalQuantity: 0, totalItems: 0 });
      } catch (err) {
        console.error('Failed to load returns report', err);
        setError(err.response?.data?.message || 'Unable to load returns report.');
        setRows([]);
        setSummary({ totalReturns: 0, totalQuantity: 0, totalItems: 0 });
      } finally {
        setLoading(false);
      }
    };
    fetchReport();
  }, [activeStore?._id, filters]);

  const exportColumns = useMemo(
    () => [
      { key: 'date', label: 'Date' },
      { key: 'supplierName', label: 'Supplier' },
      { key: 'invoiceNumber', label: 'Invoice #' },
      { key: 'itemCount', label: 'Item Count' },
      { key: 'totalQuantity', label: 'Total Qty' },
      { key: 'createdBy', label: 'Created By' },
      { key: 'notes', label: 'Notes' },
      { key: 'itemSummary', label: 'Items' },
    ],
    []
  );

  const buildExportRows = () =>
    rows.map((row) => ({
      ...row,
      date: formatDate(row.date, timezone),
    }));

  const exportCsv = () => {
    const prepared = buildExportRows();
    const csv = [
      exportColumns.map((c) => c.label).join(','),
      ...prepared.map((row) =>
        exportColumns
          .map((col) => {
            const value = row[col.key] ?? '';
            return `"${String(value).replace(/"/g, '""')}"`;
          })
          .join(',')
      ),
    ].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `returns-to-supplier-report-${activeStore?.name || 'store'}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const exportXls = () => {
    const prepared = buildExportRows();
    const header = exportColumns.map((col) => `<th>${col.label}</th>`).join('');
    const body = prepared
      .map(
        (row) =>
          `<tr>${exportColumns
            .map((col) => `<td>${row[col.key] ?? ''}</td>`)
            .join('')}</tr>`
      )
      .join('');
    const html = `<table border="1"><thead><tr>${header}</tr></thead><tbody>${body}</tbody></table>`;
    const blob = new Blob([html], { type: 'application/vnd.ms-excel' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `returns-to-supplier-report-${activeStore?.name || 'store'}.xls`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleDatePreset = (period, range) => {
    setFilters((prev) => ({
      ...prev,
      startDate: range.start,
      endDate: range.end,
    }));
  };

  const handleCustomRange = (startDate, endDate) => {
    setFilters((prev) => ({
      ...prev,
      startDate,
      endDate,
    }));
  };

  if (!activeStore) {
    return <div className="p-6 text-slate-600">Select a store to view the report.</div>;
  }

  return (
    <div className="p-6 space-y-6">
      <div className="bg-white rounded-2xl shadow p-4 space-y-4">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <DateRangeFilter
            selectedPeriod=""
            startDate={filters.startDate}
            endDate={filters.endDate}
            onSelectPreset={handleDatePreset}
            onApplyCustomRange={handleCustomRange}
          />
          <div className="flex items-center gap-2">
            <select
              value={filters.supplierId}
              onChange={(e) => setFilters((prev) => ({ ...prev, supplierId: e.target.value }))}
              className="border border-slate-300 rounded-md px-3 py-2 text-sm"
            >
              <option value="">All suppliers</option>
              {suppliers.map((supplier) => (
                <option key={supplier._id || supplier.id} value={supplier._id || supplier.id}>
                  {supplier.name}
                </option>
              ))}
            </select>
            <button onClick={exportCsv} className="px-4 py-2 bg-slate-900 text-white rounded-full text-sm font-semibold hover:bg-slate-800">
              Export CSV
            </button>
            <button onClick={exportXls} className="px-4 py-2 border border-slate-300 text-slate-700 rounded-full text-sm font-semibold hover:bg-slate-50">
              Export XLS
            </button>
          </div>
        </div>
        {error && <div className="bg-rose-50 text-rose-700 border border-rose-200 rounded px-4 py-2">{error}</div>}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-2xl shadow p-4">
          <p className="text-xs uppercase text-slate-500 tracking-wide">Returns submitted</p>
          <p className="text-2xl font-semibold text-slate-900 mt-2">{summary.totalReturns.toLocaleString()}</p>
        </div>
        <div className="bg-white rounded-2xl shadow p-4">
          <p className="text-xs uppercase text-slate-500 tracking-wide">Items returned</p>
          <p className="text-2xl font-semibold text-slate-900 mt-2">{summary.totalItems.toLocaleString()}</p>
        </div>
        <div className="bg-white rounded-2xl shadow p-4">
          <p className="text-xs uppercase text-slate-500 tracking-wide">Units returned</p>
          <p className="text-2xl font-semibold text-slate-900 mt-2">{summary.totalQuantity.toLocaleString()}</p>
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-200">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Date</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Supplier</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Invoice #</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wide">Item Count</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wide">Total Qty</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Created By</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Notes</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr>
                  <td colSpan="7" className="px-4 py-6 text-center text-slate-500">Loading...</td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-4 py-6 text-center text-slate-500">No returns found for the selected filters.</td>
                </tr>
              ) : (
                rows.map((row) => (
                  <tr key={row.id}>
                    <td className="px-4 py-2 text-sm text-slate-700">{formatDate(row.date)}</td>
                    <td className="px-4 py-2 text-sm text-slate-700">{row.supplierName}</td>
                    <td className="px-4 py-2 text-sm text-slate-700">{row.invoiceNumber || '—'}</td>
                    <td className="px-4 py-2 text-sm text-right text-slate-900">{row.itemCount.toLocaleString()}</td>
                    <td className="px-4 py-2 text-sm text-right text-slate-900">{row.totalQuantity.toLocaleString()}</td>
                    <td className="px-4 py-2 text-sm text-slate-700">{row.createdBy}</td>
                    <td className="px-4 py-2 text-sm text-slate-500">{row.notes || '—'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ReturnsToSupplierReportPage;
